export type EventParams = Record<string, string | number | boolean | undefined>;

type GtagCommand = ["event", string, Record<string, string | number | boolean>];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagCommand) => void;
    __srGaEvents?: { eventName: string; params: Record<string, string | number | boolean>; timestamp: string; source?: string }[];
    fbq?: (...args: unknown[]) => void;
  }
}

const pathPrefix: { [path: string]: string } = {
  "^\/?$": "home_",
  "^\/request\/?$": "inquiry_",
  "^\/service\/?$": "service_",
  "^\/pricing\/?$": "pricing_",
  "^\/blog\/?$": "blog_",
  "^\/blog\/.*": "blog_post_",
};

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1"]);

function isDeveloperTraffic() {
  if (process.env.NODE_ENV !== "production") return true;
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return (
    LOCAL_HOSTS.has(hostname) ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname) ||
    hostname.endsWith(".local")
  );
}

function cleanEventParams(params?: EventParams): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries({
      ...(params ?? {}),
      ...(isDeveloperTraffic() ? { debug_mode: true, traffic_type: "developer" } : {}),
    }).filter(([, value]) => value !== undefined),
  );
}

/**
 * GA4 이벤트 전송
 */
export function trackEvent(eventName: string, path: string, params?: EventParams) {
  const prefixPattern = Object.keys(pathPrefix).find((pattern) => new RegExp(pattern).test(path));
  const prefix = prefixPattern ? pathPrefix[prefixPattern] : "";

  if (typeof window !== "undefined") {
    const prefixedEventName = `${prefix}${eventName}`;
    const eventParams = cleanEventParams(params);

    window.__srGaEvents = window.__srGaEvents || [];
    window.__srGaEvents.push({ eventName: prefixedEventName, params: eventParams, timestamp: new Date().toISOString() });
    window.__srGaEvents = window.__srGaEvents.slice(-50);

    if (typeof window.gtag === "function") {
      window.gtag("event", prefixedEventName, eventParams);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(["event", prefixedEventName, eventParams]);
  }
}
